import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Delete } from "lucide-react";

// Güvenli aritmetik değerlendirme (+ - * / parantez). eval kullanmaz.
function safeEval(input) {
  const s = String(input).replace(/×/g, "*").replace(/÷/g, "/").replace(/,/g, ".").replace(/\s+/g, "");
  if (!s) return "";
  if (!/^[0-9+\-*/().]+$/.test(s)) return "Hata";
  let i = 0;
  const parseExpr = () => {
    let v = parseTerm();
    while (s[i] === "+" || s[i] === "-") { const op = s[i++]; v = op === "+" ? v + parseTerm() : v - parseTerm(); }
    return v;
  };
  const parseTerm = () => {
    let v = parseFactor();
    while (s[i] === "*" || s[i] === "/") { const op = s[i++]; const r = parseFactor(); v = op === "*" ? v * r : v / r; }
    return v;
  };
  const parseFactor = () => {
    if (s[i] === "(") { i++; const v = parseExpr(); if (s[i] === ")") i++; return v; }
    if (s[i] === "-") { i++; return -parseFactor(); }
    if (s[i] === "+") { i++; return parseFactor(); }
    let num = "";
    while (i < s.length && /[0-9.]/.test(s[i])) num += s[i++];
    return parseFloat(num || "0");
  };
  try {
    const r = parseExpr();
    if (i !== s.length || isNaN(r) || !isFinite(r)) return "Hata";
    return String(Math.round((r + Number.EPSILON) * 1e6) / 1e6);
  } catch { return "Hata"; }
}

const fmt = (v) => {
  if (v === "" || v === "Hata") return v;
  const n = Number(v);
  if (isNaN(n)) return v;
  return n.toLocaleString("tr-TR", { maximumFractionDigits: 6 });
};

export const Calculator = () => {
  const [expr, setExpr] = useState("");
  const [result, setResult] = useState("");

  useEffect(() => { setResult(expr ? safeEval(expr) : ""); }, [expr]);

  const append = (ch) => setExpr((e) => e + ch);
  const clear = () => setExpr("");
  const back = () => setExpr((e) => e.slice(0, -1));
  const equals = () => { const r = safeEval(expr); if (r !== "Hata" && r !== "") setExpr(r); };

  const keys = [
    ["7", "8", "9", "÷"],
    ["4", "5", "6", "×"],
    ["1", "2", "3", "-"],
    ["0", ".", "00", "+"],
  ];

  return (
    <div className="space-y-3" data-testid="calculator">
      <div className="bg-[#0b0f19] text-white rounded-xl p-4">
        <Input
          value={expr}
          onChange={(e) => setExpr(e.target.value.replace(/[^0-9+\-*/().,×÷ ]/g, ""))}
          onKeyDown={(e) => e.key === "Enter" && equals()}
          placeholder="0"
          className="bg-transparent border-0 text-right text-lg font-mono text-white/80 h-8 px-0 focus-visible:ring-0"
          data-testid="calc-expr"
        />
        <div className="text-right font-head font-black text-3xl tabular-nums mt-1 break-all" data-testid="calc-result">
          {result === "Hata" ? <span className="text-destructive text-xl">Hatalı işlem</span> : fmt(result) || "0"}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <Button variant="destructive" onClick={clear} data-testid="calc-clear">C</Button>
        <Button variant="outline" onClick={() => append("(")}>(</Button>
        <Button variant="outline" onClick={() => append(")")}>)</Button>
        <Button variant="outline" onClick={back} data-testid="calc-back"><Delete className="h-4 w-4" /></Button>
        {keys.map((row) =>
          row.map((k) => {
            const isOp = ["÷", "×", "-", "+"].includes(k);
            return (
              <Button
                key={k}
                variant={isOp ? "secondary" : "outline"}
                className={`h-12 text-lg font-bold ${isOp ? "bg-primary/10 text-primary hover:bg-primary/20" : ""}`}
                onClick={() => append(k)}
                data-testid={`calc-key-${k}`}
              >
                {k}
              </Button>
            );
          })
        )}
        <Button className="col-span-4 h-12 text-lg font-bold" onClick={equals} data-testid="calc-equals">=</Button>
      </div>
    </div>
  );
};
